import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Mail, ArrowRight, CheckCircle2, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useTenant, tenantBranding } from "@/hooks/useTenant";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AuthPage = ({ onLogin }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { tenant } = useTenant();
  const brand = tenantBranding(tenant);
  // When no tenant is resolved (root Thales), we're the platform's professional
  // portal — show Thales branding, not any client's brand.
  const showThalesFallback = !tenant;
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [linkSent, setLinkSent] = useState(false);
  // True when the doctor arrived here because a direct email link to a consult
  // expired/was already used — we show a reassuring message and bring them back
  // to that consult after login.
  const [fromExpiredConsult, setFromExpiredConsult] = useState(false);

  useEffect(() => {
    try {
      setFromExpiredConsult(localStorage.getItem('consult_access_expired') === '1');
    } catch {
      // ignore
    }
  }, []);

  // Auto-verify if token is in URL (from magic link email)
  useEffect(() => {
    const tokenFromUrl = searchParams.get('token');
    if (tokenFromUrl) {
      autoVerify(tokenFromUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const autoVerify = async (token) => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/auth/magic-link/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      });
      const data = await response.json();
      if (response.ok) {
        localStorage.setItem('auth_token', data.token);
        onLogin(data.user);
        toast.success("Sesion iniciada correctamente");

        // Honor intended destination saved by ProtectedRoute, when applicable.
        let redirectTo = null;
        try {
          redirectTo = localStorage.getItem('auth_redirect');
          if (redirectTo) localStorage.removeItem('auth_redirect');
          localStorage.removeItem('consult_access_expired');
        } catch {
          // ignore
        }

        if (redirectTo && data.user?.role !== 'ADMIN') {
          navigate(redirectTo);
        } else if (data.user?.role === 'ADMIN') {
          navigate('/admin');
        } else {
          navigate('/dashboard');
        }
      } else {
        toast.error(data.detail || "El enlace es invalido o ya fue utilizado");
      }
    } catch (error) {
      toast.error("Error al conectar con el servidor");
    } finally {
      setLoading(false);
    }
  };

  const handleSendMagicLink = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/auth/magic-link/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (response.ok) {
        setLinkSent(true);
        toast.success("Te enviamos un enlace de acceso");
      } else {
        toast.error(data.detail || "Error al enviar el enlace de acceso");
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error("Error al conectar con el servidor");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <nav className="border-b border-slate-200 bg-white/70 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/')} data-testid="auth-nav-logo">
              {showThalesFallback ? (
                <>
                  <img src="/logo-thales.png" alt="Thales Health" className="h-8 sm:h-9 w-auto" />
                  <span
                    className="font-semibold text-base sm:text-lg tracking-tight text-slate-900"
                    style={{ fontFamily: "'Manrope', sans-serif" }}
                  >
                    Thales Health
                  </span>
                </>
              ) : brand.logoUrl ? (
                <img src={brand.logoUrl} alt={brand.name} className="h-10 sm:h-12 object-contain" />
              ) : (
                <span
                  className="font-semibold text-base sm:text-lg tracking-tight"
                  style={{ color: brand.primaryColor, fontFamily: "'Manrope', sans-serif" }}
                >
                  {brand.name}
                </span>
              )}
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-md mx-auto px-4 py-16">
        <div className="bg-white rounded-xl border border-slate-200 p-8 md:p-10 shadow-sm">
          <div className="text-center mb-8">
            <div className="h-16 w-16 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Mail className="h-8 w-8 text-accent" />
            </div>
            <h1 className="text-3xl font-bold text-primary mb-2">
              Portal Médico
            </h1>
            <p className="text-slate-600">
              Ingresa tu email para recibir un enlace de acceso
            </p>
          </div>

          {fromExpiredConsult && !linkSent && (
            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg flex items-start gap-2.5" data-testid="consult-access-expired-banner">
              <AlertCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-blue-900">
                Por seguridad, el acceso directo del email caducó. Ingresá con tu
                correo y <strong>te llevamos directo a la consulta</strong> — sigue
                esperando en tu bandeja.
              </p>
            </div>
          )}

          {!linkSent ? (
            <form onSubmit={handleSendMagicLink} data-testid="magic-link-form">
              <div className="mb-6">
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  data-testid="auth-email-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="doctor@institucion.com"
                  required
                  className="h-12"
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full h-12 rounded-full btn-hover"
                data-testid="send-magic-link-btn"
              >
                {loading ? "Enviando..." : "Enviar enlace de acceso"}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </form>
          ) : (
            <div className="space-y-4" data-testid="magic-link-sent">
              <div className="p-5 bg-emerald-50 border border-emerald-200 rounded-lg text-center">
                <div className="h-12 w-12 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <CheckCircle2 className="h-6 w-6 text-emerald-600" />
                </div>
                <p className="text-sm font-semibold text-emerald-900 mb-2">
                  Enlace enviado a tu email
                </p>
                <p className="text-sm text-emerald-800">
                  Revisá tu casilla <strong>{email}</strong> y hacé click en el enlace para acceder al portal. El link expira en 30 minutos.
                </p>
              </div>

              <div className="p-3 bg-amber-50 border border-amber-300 rounded-lg flex items-start gap-2.5" data-testid="spam-alert">
                <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-amber-900">
                  <strong>¿No te llegó?</strong> Revisá tu carpeta de <strong>spam o correo no deseado</strong>. Los correos corporativos pueden tardar hasta 2 minutos.
                </p>
              </div>

              <Button
                variant="outline"
                onClick={() => {
                  setLinkSent(false);
                  setEmail("");
                }}
                className="w-full h-12 rounded-full"
                data-testid="reset-magic-link-btn"
              >
                Usar otro email
              </Button>
            </div>
          )}

          <div className="mt-6 text-center">
            <p className="text-sm text-slate-500 mb-4">
              {showThalesFallback
                ? "Acceso para profesionales médicos adheridos a la plataforma"
                : `Acceso para médicos verificados de ${brand.name}`}
            </p>
            <div className="pt-4 border-t border-slate-200">
              <p className="text-sm text-slate-600 mb-2">¿Eres médico y quieres unirte?</p>
              <Button
                variant="outline"
                onClick={() => navigate('/doctor/register')}
                className="w-full"
                data-testid="request-access-btn"
              >
                Solicitar acceso
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthPage;
